import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, Circle } from 'react-leaflet';
// react-leaflet-cluster v2 ships ESM-only and has no .d.ts entry on this
// version — declared in src/types/react-leaflet-cluster.d.ts.
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { Dialog } from '@headlessui/react';
import { vehiclesApi, alertsApi, stationPointsApi, StationPoint } from '@/services/api';
import { VEHICLE_ONLINE_THRESHOLD_MS } from '@/constants/connectivity';
import {
  vehicleTypeSvg,
  vehicleTypeLabel,
  vehicleMarkerColor,
} from '@/constants/vehicleTypes';
import { useSocketEvent } from '@/services/socket';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  MapPinIcon,
  TruckIcon,
  BuildingOffice2Icon,
  SignalIcon,
  ClockIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  EyeIcon,
  CalendarDaysIcon,
  WifiIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  DocumentTextIcon,
  XMarkIcon,
  Bars3Icon,
  ExclamationCircleIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ViewfinderCircleIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ar';

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ar');

const TZ = 'Africa/Tripoli';
const fmtTime = (input: string | Date | undefined | null, pattern = 'HH:mm:ss') =>
  input ? dayjs(input).tz(TZ).format(pattern) : '—';

/**
 * When we last heard from a vehicle, for anything labelled "آخر اتصال".
 *
 * Falls back to the device clock only when the server sent no receipt time —
 * which the column default should make impossible, but an old cached payload
 * is better shown late than not at all.
 */
const heardAt = (p: { heardAt?: string; timestamp: string }): string =>
  p.heardAt || p.timestamp;
// Persisted map view (so reload doesn't reset the user's pan/zoom).
const MAP_STATE_KEY = 'livemap-view';
const readMapState = (): { center: [number, number]; zoom: number } | null => {
  try {
    const raw = localStorage.getItem(MAP_STATE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (Array.isArray(v.center) && typeof v.zoom === 'number') return v;
  } catch { /* ignore */ }
  return null;
};
const saveMapState = (center: [number, number], zoom: number) => {
  try { localStorage.setItem(MAP_STATE_KEY, JSON.stringify({ center, zoom })); } catch { /* ignore */ }
};

// Color a polyline segment by speed bucket — buckets tuned for highway
// speeds in Libya (typical city max ~80, highway 100-120). Below 100 is
// safe (green), 100-119 is a caution zone (yellow), 120+ is over the
// alert threshold (red).
const speedColor = (speed?: number): string => {
  if (speed == null) return '#3B82F6';   // blue for missing speed
  if (speed < 100) return '#10B981';     // green — safe
  if (speed < 120) return '#F59E0B';     // yellow — caution (100-119)
  return '#EF4444';                      // red — over limit (>= 120)
};

// Fix Leaflet default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom ambulance icon - now uses isOnline for connection status
// Fixed ground positions (stations, gates, staging points). Deliberately
// square with a flat bottom so they never read as a vehicle pin at a glance,
// and smaller so a corridor of 24 does not swamp the live fleet.
const STATION_STYLE: Record<string, { color: string; label: string }> = {
  ambulance_point: { color: '#dc2626', label: 'نقطة إسعاف' },
  checkpoint:      { color: '#dc2626', label: 'بوابة / تمركز' },
  staging_point:   { color: '#dc2626', label: 'نقطة تمركز' },
};

// A red "H" — the standard mark for a hospital/station position, readable at
// marker size without relying on a symbol whose use is restricted.
const createStationIcon = (kind: string) => {
  const style = STATION_STYLE[kind] || STATION_STYLE.checkpoint;
  return L.divIcon({
    className: 'custom-station-marker',
    html: `
      <div style="
        background-color: white;
        color: ${style!.color};
        width: 28px;
        height: 28px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid ${style!.color};
        box-shadow: 0 1px 5px rgba(0,0,0,0.35);
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
        font-size: 18px;
        line-height: 1;
      ">H</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  });
};

// Glyph carries the vehicle's class, fill carries its status. Previously the
// fill tested for 'active'/'busy', which are not values the vehicle_status
// enum has, so every online vehicle rendered the same blue.
/**
 * Icons are cached and shared between markers, and that is load-bearing.
 *
 * react-leaflet compares the `icon` prop by REFERENCE
 * (`react-leaflet/lib/Marker.js`: `props.icon !== prevProps.icon → setIcon`).
 * Building a fresh `L.divIcon` inside the render call meant `setIcon` fired on
 * every marker on every render — measured at 9 calls per render, every 5
 * seconds. Inside a MarkerClusterGroup that removes and re-adds the marker,
 * and Leaflet binds `closePopup` to a layer's `remove` event, so an open
 * popup blinked shut and back open on a 5-second cycle. Measured before the
 * fix: popup-CLOSE/popup-OPEN pairs at exactly that cadence.
 *
 * Sharing one DivIcon across markers is safe — Leaflet builds a separate DOM
 * node per marker from the html string. The key space is bounded by the data
 * (6 classes × 5 statuses × online × selected), so the cache cannot grow
 * without limit and needs no eviction.
 */
const VEHICLE_ICON_CACHE = new Map<string, L.DivIcon>();

const createVehicleIcon = (
  vehicleType: string,
  status: string,
  isOnline: boolean,
): L.DivIcon => {
  const cacheKey = `${vehicleType}|${status}|${isOnline}`;
  const cached = VEHICLE_ICON_CACHE.get(cacheKey);
  if (cached) return cached;
  const icon = buildVehicleIcon(vehicleType, status, isOnline);
  VEHICLE_ICON_CACHE.set(cacheKey, icon);
  return icon;
};

const buildVehicleIcon = (
  vehicleType: string,
  status: string,
  isOnline: boolean,
) => {
  const color = vehicleMarkerColor(status, isOnline);
  // 42px with a 30px drawing: the previous 36/20 left the silhouette too
  // small to tell a van from a saloon at normal map zoom.
  const dot = 42;

  return L.divIcon({
    className: 'custom-vehicle-marker',
    html: `
      <div style="
        background-color: ${color};
        width: ${dot}px;
        height: ${dot}px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.35);
        position: relative;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30">
          ${vehicleTypeSvg(vehicleType, '#ffffff', color)}
        </svg>
        ${isOnline ? `<div style="
          position: absolute;
          top: -1px;
          right: -1px;
          width: 11px;
          height: 11px;
          background-color: #10B981;
          border: 2px solid white;
          border-radius: 50%;
        "></div>` : ''}
      </div>`,
    iconSize: [dot, dot],
    iconAnchor: [dot / 2, dot / 2],
    popupAnchor: [0, -(dot / 2) - 2],
  });
};

/**
 * The selection ring is its own map layer, not part of the vehicle's icon.
 *
 * Baking it into the icon meant selecting a vehicle changed that marker's
 * icon, and inside a cluster group changing an icon removes and re-adds the
 * marker — which closes any popup bound to it. Drawing the ring as a separate,
 * non-interactive marker leaves the vehicle marker's identity untouched, so
 * selecting a unit no longer disturbs its open popup.
 */
const SELECTION_HALO_SIZE = 66;
const SELECTION_HALO_ICON = L.divIcon({
  className: 'vehicle-selection-halo',
  html: '<div class="vehicle-marker-halo"></div><div class="vehicle-marker-ring"></div>',
  iconSize: [SELECTION_HALO_SIZE, SELECTION_HALO_SIZE],
  iconAnchor: [SELECTION_HALO_SIZE / 2, SELECTION_HALO_SIZE / 2],
});

// Map center controller component - supports auto-fit to all vehicles
function MapController({
  center,
  zoom,
  shouldUpdate,
  onUpdateComplete,
  fitBounds,
  onFitComplete,
  onUserMove,
  onMapReady,
}: {
  center: [number, number];
  zoom: number;
  shouldUpdate: boolean;
  onUpdateComplete: () => void;
  fitBounds?: L.LatLngBoundsExpression | null;
  onFitComplete?: () => void;
  /** Called when the user pans/zooms so the parent can persist the view. */
  onUserMove?: (center: [number, number], zoom: number) => void;
  /** Hands the underlying Leaflet map instance up to the parent so it can
   *  call .invalidateSize() after fullscreen / layout changes. */
  onMapReady?: (map: L.Map) => void;
}) {
  const map = useMap();

  useEffect(() => {
    onMapReady?.(map);
  }, [map, onMapReady]);

  useEffect(() => {
    if (shouldUpdate && center) {
      map.setView(center, zoom);
      onUpdateComplete();
    }
  }, [center, zoom, shouldUpdate, map, onUpdateComplete]);

  useEffect(() => {
    if (fitBounds) {
      map.fitBounds(fitBounds, { padding: [50, 50], maxZoom: 16 });
      onFitComplete?.();
    }
  }, [fitBounds, map, onFitComplete]);

  // Persist user-initiated pan/zoom (debounced via moveend event).
  useEffect(() => {
    if (!onUserMove) return;
    const handle = () => {
      const c = map.getCenter();
      onUserMove([c.lat, c.lng], map.getZoom());
    };
    map.on('moveend', handle);
    return () => { map.off('moveend', handle); };
  }, [map, onUserMove]);

  return null;
}

interface Vehicle {
  id: string;
  plateNumber: string;
  vehicleType: string;
  status: string;
  // Shown wherever a unit is inspected — two identical plates of different
  // makes are common in a mixed fleet, and the model tells them apart.
  model?: string | null;
  year?: number | null;
  organizationId?: string;
  // Eager-loaded org relation from /vehicles/with-positions —
  // surfaced in the sidebar list and the marker popup so the operator
  // can see which hospital/department a unit belongs to.
  organization?: {
    id: string;
    name: string;
    nameAr?: string;
  } | null;
  latestPosition?: {
    id: number;
    vehicleId: string;
    latitude: string;
    longitude: string;
    speed?: string;
    course?: string;
    deviceTime: string;
    serverTime: string;
  };
}

interface Alert {
  id: string;
  type: string;
  severity: string;
  message: string;
  messageAr?: string;
  vehicleId: string;
  latitude?: string;
  longitude?: string;
  status: string;
  createdAt: string;
  vehicle?: {
    plateNumber: string;
    vehicleType: string;
  };
}

interface PositionUpdate {
  vehicleId: string;
  latitude: number;
  longitude: number;
  speed?: number;
  course?: number;
  /** Device clock — when the fix was taken. Right for plotting a track. */
  timestamp: string;
  /**
   * Our clock — when the packet reached us. Everything labelled "آخر اتصال"
   * has to read this: a tracker whose RTC sits on UTC while we run UTC+2
   * stamps its fixes two hours behind, and a device replaying a buffered
   * backlog sends old device times while connected this second. Both made
   * live vehicles look like they had not been heard from in hours.
   */
  heardAt?: string;
  plateNumber?: string;
}

// Route history for each vehicle
interface VehicleRoute {
  vehicleId: string;
  positions: Array<{ lat: number; lng: number; timestamp: string; speed?: number }>;
}

// Online status tracking
interface OnlineStatus {
  vehicleId: string;
  lastSeen: Date;
  isOnline: boolean;
}

// History statistics
interface RouteStatistics {
  totalPoints: number;
  totalDistance: number; // in km
  avgSpeed: number; // in km/h
  maxSpeed: number; // in km/h
  totalDuration: number; // in minutes
  startTime: string;
  endTime: string;
}

// Consider vehicle online if last update was within 60 seconds
// Threshold shared with the dashboard and the server — see constants/connectivity.
const ONLINE_THRESHOLD_MS = VEHICLE_ONLINE_THRESHOLD_MS;

// Calculate distance between two points using Haversine formula
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Calculate route statistics
function calculateRouteStatistics(positions: Array<{ lat: number; lng: number; timestamp: string; speed?: number }>): RouteStatistics | null {
  if (positions.length < 2) return null;

  let totalDistance = 0;
  let totalSpeed = 0;
  let maxSpeed = 0;
  let speedCount = 0;

  for (let i = 1; i < positions.length; i++) {
    const prev = positions[i - 1];
    const curr = positions[i];
    totalDistance += calculateDistance(prev.lat, prev.lng, curr.lat, curr.lng);
    
    if (curr.speed !== undefined) {
      totalSpeed += curr.speed;
      maxSpeed = Math.max(maxSpeed, curr.speed);
      speedCount++;
    }
  }

  const startTime = dayjs(positions[0].timestamp);
  const endTime = dayjs(positions[positions.length - 1].timestamp);
  const totalDuration = endTime.diff(startTime, 'minute');

  return {
    totalPoints: positions.length,
    totalDistance: Math.round(totalDistance * 100) / 100,
    avgSpeed: speedCount > 0 ? Math.round(totalSpeed / speedCount) : Math.round((totalDistance / (totalDuration / 60)) || 0),
    maxSpeed: Math.round(maxSpeed),
    totalDuration,
    startTime: startTime.format('YYYY-MM-DD HH:mm'),
    endTime: endTime.format('YYYY-MM-DD HH:mm'),
  };
}

export default function LiveMap() {
  const persistedView = readMapState();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(
    // Arriving from the vehicles page: /map?vehicle=<id> opens already
    // pointed at that unit instead of making the operator hunt for it.
    () => searchParams.get('vehicle'),
  );
  const [mapCenter, setMapCenter] = useState<[number, number]>(persistedView?.center || [32.1194, 20.0868]); // بنغازي
  const [mapZoom, setMapZoom] = useState(persistedView?.zoom || 12);
  const [shouldUpdateMap, setShouldUpdateMap] = useState(false);
  const [vehiclePositions, setVehiclePositions] = useState<Map<string, PositionUpdate>>(new Map());
  const [isTracking, setIsTracking] = useState(true);
  // Live tail per vehicle — appended on every websocket update, capped at 100 points.
  const [vehicleRoutes, setVehicleRoutes] = useState<Map<string, VehicleRoute>>(new Map());
  // Historical trace per vehicle — loaded once from /history endpoint, immutable
  // until cleared. Keeping it separate from vehicleRoutes prevents live updates
  // from truncating the history to 100 points (the previous flash bug).
  const [historyRoutes, setHistoryRoutes] = useState<Map<string, VehicleRoute>>(new Map());
  // Alerts collected during the chosen history window, per vehicle. Rendered
  // as severity-coloured circles on the map. Cleared when leaving history mode.
  const [historyAlerts, setHistoryAlerts] = useState<Map<string, Alert[]>>(new Map());
  const [showRoute, setShowRoute] = useState<string | null>(null);
  // Live "active alerts" state was removed — alerts are no longer rendered
  // in the sidebar. We still wire the WebSocket alert:new event so the
  // critical-severity audio cue keeps working.
  const [onlineStatuses, setOnlineStatuses] = useState<Map<string, OnlineStatus>>(new Map());
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyVehicleId, setHistoryVehicleId] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<Array<{ lat: number; lng: number; timestamp: string; speed?: number }>>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyDateRange, setHistoryDateRange] = useState({
    start: dayjs().subtract(1, 'day').format('YYYY-MM-DDTHH:mm'),
    end: dayjs().format('YYYY-MM-DDTHH:mm'),
  });
  const [showStatisticsPanel, setShowStatisticsPanel] = useState(false);
  const [routeStatistics, setRouteStatistics] = useState<RouteStatistics | null>(null);
  const [fitBoundsData, setFitBoundsData] = useState<L.LatLngBoundsExpression | null>(null);
  // A deep link (?vehicle=) suppresses the fit-to-whole-fleet on first load;
  // otherwise the camera would frame every unit and immediately fight the
  // zoom onto the one the operator actually asked for.
  const [hasInitialFit, setHasInitialFit] = useState(() => Boolean(searchParams.get('vehicle')));
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  // UI toggles for new features.
  // Fixed station/deployment layer. Persisted because an operator watching a
  // corridor wants it to stay on across reloads.
  const [showStations, setShowStations] = useState<boolean>(() => {
    try { return localStorage.getItem('map-show-stations') !== 'false'; } catch { return true; }
  });
  const [stationKindFilter, setStationKindFilter] = useState<string>('');

  useEffect(() => {
    try { localStorage.setItem('map-show-stations', String(showStations)); } catch { /* private mode */ }
  }, [showStations]);

  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobileStatsOpen, setMobileStatsOpen] = useState(false);
  const [autoFollow, setAutoFollow] = useState<string | null>(null);  // vehicleId being followed
  const [colorBySpeed, setColorBySpeed] = useState(true);
  const [enableClustering, setEnableClustering] = useState(true);
  // Audio toggle — persisted so the user's mute preference survives reload.
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem('livemap-sound') !== 'off'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('livemap-sound', soundEnabled ? 'on' : 'off'); } catch { /* ignore */ }
  }, [soundEnabled]);
  // Fullscreen the entire map wrapper (the flex-1 relative div), not the
  // page. Tracked as state so the toggle button can flip its icon, and
  // synchronised with the system fullscreenchange event so pressing Esc
  // updates the icon correctly.
  const mapWrapRef = useRef<HTMLDivElement | null>(null);
  const leafletMapRef = useRef<L.Map | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onFs = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Leaflet caches viewport size; invalidate after layout changes.
      setTimeout(() => leafletMapRef.current?.invalidateSize(), 200);
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mapWrapRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  // Calculate statistics when history data changes
  useEffect(() => {
    if (historyData.length > 0) {
      const stats = calculateRouteStatistics(historyData);
      setRouteStatistics(stats);
    } else {
      setRouteStatistics(null);
    }
  }, [historyData]);

  // Check online status periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setOnlineStatuses(prev => {
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        prev.forEach((status, vehicleId) => {
          const isOnline = now - status.lastSeen.getTime() < ONLINE_THRESHOLD_MS;
          if (status.isOnline !== isOnline) {
            next.set(vehicleId, { ...status, isOnline });
            changed = true;
          }
        });
        // Returning a new Map unconditionally re-rendered the whole page every
        // five seconds even when no vehicle had changed state — which is what
        // drove the marker/popup churn. Same reference means no render.
        return changed ? next : prev;
      });
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Fetch vehicles with positions.
  // Force fresh fetch on every mount (stale cache from a previous visit
  // can return zero positions even when the device sent updates while
  // we were on another page). retry: 2 with fixed delay so a transient
  // 429/5xx doesn't leave the user staring at a blank map.
  const { data: vehiclesData, isLoading, refetch, isError, error } = useQuery({
    queryKey: ['vehicles-with-positions'],
    queryFn: async () => {
      const response = await vehiclesApi.getWithPositions();
      return response.data;
    },
    refetchInterval: isTracking ? 60000 : false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    // Was `retry: 2, retryDelay: 800` — three attempts 800 ms apart, all inside
    // the same rate-limit window. The global retry policy now skips 4xx, so a
    // 429 here is no longer tripled.
    retry: (failureCount: number, error: any) => {
      const status = error?.response?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
    retryDelay: 800,
    staleTime: 0,
  });

  // Fixed stations and deployment points. These barely change, so they are
  // cached hard rather than polled alongside the fleet.
  const { data: stationData } = useQuery({
    queryKey: ['station-points'],
    queryFn: async () => {
      const r = await stationPointsApi.getAll();
      return {
        items: (r.data.data ?? []) as StationPoint[],
        meta: r.data.meta ?? { corridors: [], byKind: {} },
      };
    },
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const visibleStations = useMemo(() => {
    const items = stationData?.items ?? [];
    if (!stationKindFilter) return items;
    return items.filter((sp) => sp.kind === stationKindFilter);
  }, [stationData, stationKindFilter]);

  // Active alerts polling removed — they don't render anywhere in the
  // LiveMap UI any more. Alerts are fetched on demand only when the
  // user opens a history window (see loadHistoryData).

  // Initialize vehicle positions from query data
  useEffect(() => {
    if (vehiclesData?.data && Array.isArray(vehiclesData.data)) {
      const newPositions = new Map<string, PositionUpdate>();
      const newOnlineStatuses = new Map<string, OnlineStatus>();
      const now = new Date();
      
      vehiclesData.data.forEach((vehicle: Vehicle) => {
        // Use latestPosition from API response
        if (vehicle.latestPosition) {
          const lat = parseFloat(vehicle.latestPosition.latitude);
          const lng = parseFloat(vehicle.latestPosition.longitude);
          const speed = vehicle.latestPosition.speed ? parseFloat(vehicle.latestPosition.speed) : undefined;
          const course = vehicle.latestPosition.course ? parseFloat(vehicle.latestPosition.course) : undefined;
          // Position drawn from the device clock, but freshness judged on
          // ours: a tracker with a drifted RTC is still connected.
          const timestamp = vehicle.latestPosition.deviceTime || vehicle.latestPosition.serverTime;
          const heardAt = vehicle.latestPosition.serverTime || timestamp;
          
          newPositions.set(vehicle.id, {
            vehicleId: vehicle.id,
            latitude: lat,
            longitude: lng,
            speed,
            course,
            timestamp,
            // Prefer the server's own lastSeenAt when it sent one — the same
            // value the dashboard and vehicles list count connectivity from.
            heardAt: (vehicle as any).lastSeenAt || heardAt,
            plateNumber: vehicle.plateNumber,
          });
          
          // Calculate online status based on last position timestamp
          const lastSeen = new Date(heardAt);
          const timeSinceLastSeen = now.getTime() - lastSeen.getTime();
          newOnlineStatuses.set(vehicle.id, {
            vehicleId: vehicle.id,
            lastSeen,
            // Prefer the server's verdict when it sent one; it is the same
            // figure the dashboard and the vehicles list are showing.
            isOnline: typeof (vehicle as any).isOnline === 'boolean'
              ? (vehicle as any).isOnline
              : timeSinceLastSeen < ONLINE_THRESHOLD_MS,
          });
        }
      });
      
      // Always update positions when data changes
      setVehiclePositions(newPositions);
      setOnlineStatuses(prev => {
        // Merge with existing statuses to preserve real-time updates
        const merged = new Map(prev);
        newOnlineStatuses.forEach((status, id) => {
          if (!merged.has(id) || merged.get(id)!.lastSeen < status.lastSeen) {
            merged.set(id, status);
          }
        });
        return merged;
      });

      // Auto-fit to all vehicles on initial load only
      if (!hasInitialFit && newPositions.size > 0) {
        const positions = Array.from(newPositions.values());
        if (positions.length === 1) {
          // Single vehicle - center on it
          setMapCenter([positions[0].latitude, positions[0].longitude]);
          setMapZoom(14);
          setShouldUpdateMap(true);
        } else if (positions.length > 1) {
          // Multiple vehicles - fit bounds
          const bounds = positions.map(p => [p.latitude, p.longitude] as [number, number]);
          setFitBoundsData(bounds);
        }
        setHasInitialFit(true);
      }
    }
  }, [vehiclesData, hasInitialFit]);

  // Handle real-time position updates.
  // CRITICAL: this only touches `vehicleRoutes` (the 100-point live tail).
  // It must NEVER touch `historyRoutes` — that was the cause of the
  // polyline flash bug where a 500-point trace got truncated to 100 on each
  // websocket frame.
  const handlePositionUpdate = useCallback((data: PositionUpdate) => {
    if (!isTracking) return;

    const now = new Date();

    setVehiclePositions(prev => {
      const newMap = new Map(prev);
      // The packet arrived just now, so that is the contact time — used
      // whenever an older server build doesn't stamp one.
      newMap.set(data.vehicleId, {
        ...data,
        heardAt: data.heardAt || now.toISOString(),
      });
      return newMap;
    });

    setOnlineStatuses(prev => {
      const newMap = new Map(prev);
      newMap.set(data.vehicleId, {
        vehicleId: data.vehicleId,
        lastSeen: now,
        isOnline: true,
      });
      return newMap;
    });

    setVehicleRoutes(prev => {
      const newMap = new Map(prev);
      const existingRoute = newMap.get(data.vehicleId) || { vehicleId: data.vehicleId, positions: [] };
      const newPosition = { lat: data.latitude, lng: data.longitude, timestamp: data.timestamp, speed: data.speed };
      const positions = [...existingRoute.positions, newPosition].slice(-100);
      newMap.set(data.vehicleId, { ...existingRoute, positions });
      return newMap;
    });

    // Auto-follow: keep the camera centered on the followed vehicle.
    if (autoFollow === data.vehicleId) {
      setMapCenter([data.latitude, data.longitude]);
      setShouldUpdateMap(true);
    }
  }, [isTracking, autoFollow]);

  // The audio cue now lives in useCriticalAlertSound, mounted in MainLayout,
  // so it sounds on every page instead of only while the map is open. The
  // mute toggle below still writes the same `livemap-sound` key that hook
  // reads, so one switch governs both.

  // Subscribe to socket events
  useSocketEvent('position:update', handlePositionUpdate);

  const vehicles = vehiclesData?.data || [];

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle: Vehicle) =>
      vehicle.plateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.vehicleType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicleTypeLabel(vehicle.vehicleType).includes(searchTerm)
    );
  }, [vehicles, searchTerm]);

  // Get vehicle position from real-time updates or initial data
  const getVehiclePosition = useCallback((vehicleId: string) => {
    return vehiclePositions.get(vehicleId);
  }, [vehiclePositions]);

  /**
   * A stable [lat, lng] tuple per vehicle.
   *
   * react-leaflet compares `position` by reference too, so a fresh array each
   * render called `setLatLng` on every marker even when nothing had moved —
   * more needless work for the cluster group to react to. Same coordinates in,
   * same array out.
   */
  const posTuples = useRef(new Map<string, [number, number]>());
  const posTuple = useCallback((id: string, lat: number, lng: number): [number, number] => {
    const prev = posTuples.current.get(id);
    if (prev && prev[0] === lat && prev[1] === lng) return prev;
    const next: [number, number] = [lat, lng];
    posTuples.current.set(id, next);
    return next;
  }, []);

  // Check if vehicle is online
  const isVehicleOnline = useCallback((vehicleId: string) => {
    const status = onlineStatuses.get(vehicleId);
    return status?.isOnline || false;
  }, [onlineStatuses]);

  // Count online vehicles
  const onlineCount = useMemo(() => {
    let count = 0;
    onlineStatuses.forEach(status => {
      if (status.isOnline) count++;
    });
    return count;
  }, [onlineStatuses]);

  // Count offline vehicles
  const offlineCount = useMemo(() => {
    return vehicles.length - onlineCount;
  }, [vehicles.length, onlineCount]);

  const handleVehicleSelect = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle.id);
    const position = getVehiclePosition(vehicle.id);
    if (position) {
      setMapCenter([position.latitude, position.longitude]);
      setMapZoom(16);
      setShouldUpdateMap(true);
    }
  };

  const centerOnVehicle = (vehicleId: string) => {
    const position = getVehiclePosition(vehicleId);
    if (position) {
      setMapCenter([position.latitude, position.longitude]);
      setMapZoom(16);
      setShouldUpdateMap(true);
    }
  };

  const handleMapUpdateComplete = useCallback(() => {
    setShouldUpdateMap(false);
  }, []);

  /**
   * Deep link from the vehicles page: /map?vehicle=<id>.
   *
   * Waits for that vehicle's position to arrive rather than firing on mount —
   * the fleet is fetched asynchronously, and centring on nothing would just
   * leave the camera at the default view.
   */
  useEffect(() => {
    const id = searchParams.get('vehicle');
    if (!id || deepLinkHandled) return;
    const position = vehiclePositions.get(id);
    if (!position) return;

    setSelectedVehicle(id);
    setMapCenter([position.latitude, position.longitude]);
    setMapZoom(16);
    setShouldUpdateMap(true);
    setDeepLinkHandled(true);

    // Consume the parameter, so panning away and reloading doesn't yank the
    // camera back to this unit.
    const next = new URLSearchParams(searchParams);
    next.delete('vehicle');
    setSearchParams(next, { replace: true });
  }, [searchParams, vehiclePositions, deepLinkHandled, setSearchParams]);

  /**
   * Keep the sidebar in step with the selection.
   *
   * Clicking a marker used to change nothing in the list, so the operator had
   * to type the plate into the search box to see that unit's details. Now the
   * list scrolls to it — and if an active search filters it out, that search
   * is cleared, because a hidden row cannot be scrolled to.
   */
  useEffect(() => {
    if (!selectedVehicle) return;
    if (searchTerm && !filteredVehicles.some((v: Vehicle) => v.id === selectedVehicle)) {
      setSearchTerm('');
      return;
    }
    // One frame later: the row may have only just been rendered.
    const timer = window.setTimeout(() => {
      document
        .querySelectorAll(`[data-vehicle-row="${selectedVehicle}"]`)
        .forEach((el) => el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
    }, 60);
    return () => window.clearTimeout(timer);
  }, [selectedVehicle, searchTerm, filteredVehicles]);

  // Get alert icon based on type
  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'sos': return '🆘';
      case 'speeding': return '🚨';
      case 'low_battery': return '🔋';
      case 'power_cut': return '⚡';
      case 'connection_lost': return '📡';
      case 'connection_restored': return '✅';
      case 'geofence_exit': return '🚧';
      case 'geofence_enter': return '📍';
      default: return '⚠️';
    }
  };

  // Hex color for Leaflet Circle stroke/fill. Critical=red, high=orange,
  // medium=yellow, low (or anything else)=green.
  const severityHex = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#DC2626';
      case 'high':     return '#F97316';
      case 'medium':   return '#F59E0B';
      default:         return '#10B981';
    }
  };

  // Rank severities so dedup can pick the worst when multiple alerts collide
  // on the same coordinate.
  const severityRank = (severity: string): number => {
    switch (severity) {
      case 'critical': return 4;
      case 'high':     return 3;
      case 'medium':   return 2;
      default:         return 1;
    }
  };

  // Load history into the dedicated historyRoutes map (not vehicleRoutes), so
  // live websocket updates can't truncate the trace. Auto-fits the camera to
  // the full route so zoom-out shows start-to-end.
  const loadHistoryData = async (vehicleId: string) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await vehiclesApi.getPositionHistory(
        vehicleId,
        new Date(historyDateRange.start).toISOString(),
        new Date(historyDateRange.end).toISOString()
      );

      if (response.data?.data) {
        type HistPt = { lat: number; lng: number; timestamp: string; speed?: number };
        const positions: HistPt[] = response.data.data.map((p: any) => ({
          lat: parseFloat(p.latitude),
          lng: parseFloat(p.longitude),
          timestamp: p.deviceTime,
          speed: p.speed ? parseFloat(p.speed) : undefined,
        }));
        setHistoryData(positions);

        if (positions.length === 0) {
          setHistoryError('لا توجد نقاط مسجّلة في هذه الفترة');
          return;
        }

        // Write to the dedicated history map.
        setHistoryRoutes(prev => {
          const newMap = new Map(prev);
          newMap.set(vehicleId, { vehicleId, positions });
          return newMap;
        });
        setShowRoute(vehicleId);
        setShowStatisticsPanel(true);

        // Fit camera to the entire route.
        if (positions.length === 1) {
          setMapCenter([positions[0].lat, positions[0].lng]);
          setMapZoom(15);
          setShouldUpdateMap(true);
        } else {
          const bounds = positions.map(p => [p.lat, p.lng] as [number, number]);
          setFitBoundsData(bounds);
        }

        // Following live + showing history at the same time is confusing —
        // pause auto-follow on the followed vehicle.
        if (autoFollow === vehicleId) setAutoFollow(null);

        // Fetch alerts for the same time window so the trace can show what
        // happened where. Best-effort — if alerts API fails we still show
        // the route. The 'all' status pulls active+acknowledged+resolved.
        try {
          const alertsResp = await alertsApi.getAll({
            vehicleId,
            startDate: new Date(historyDateRange.start).toISOString(),
            endDate: new Date(historyDateRange.end).toISOString(),
            limit: 500,
          });
          const fetched: Alert[] = (alertsResp.data?.data || []).filter(
            (a: Alert) => a.latitude && a.longitude
          );
          setHistoryAlerts(prev => {
            const newMap = new Map(prev);
            newMap.set(vehicleId, fetched);
            return newMap;
          });
        } catch (alertErr) {
          // Non-fatal — log and continue.
          console.warn('Failed to load alerts for history window:', alertErr);
        }

        setShowHistoryModal(false);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'فشل تحميل السجل';
      setHistoryError(msg);
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Clear both history and live tail for a vehicle.
  const clearAllRoutes = useCallback((vehicleId: string) => {
    setHistoryRoutes(prev => {
      const newMap = new Map(prev);
      newMap.delete(vehicleId);
      return newMap;
    });
    setVehicleRoutes(prev => {
      const newMap = new Map(prev);
      newMap.delete(vehicleId);
      return newMap;
    });
    setHistoryAlerts(prev => {
      const newMap = new Map(prev);
      newMap.delete(vehicleId);
      return newMap;
    });
    if (showRoute === vehicleId) setShowRoute(null);
    setShowStatisticsPanel(false);
    setRouteStatistics(null);
    setHistoryData([]);
  }, [showRoute]);

  // Drop history only, keep building a fresh live tail from now on.
  const exitHistoryMode = useCallback((vehicleId: string) => {
    setHistoryRoutes(prev => {
      const newMap = new Map(prev);
      newMap.delete(vehicleId);
      return newMap;
    });
    setHistoryAlerts(prev => {
      const newMap = new Map(prev);
      newMap.delete(vehicleId);
      return newMap;
    });
    setHistoryData([]);
    setRouteStatistics(null);
    setShowStatisticsPanel(false);
  }, []);

  // Open history modal
  const openHistoryModal = (vehicleId: string) => {
    setHistoryVehicleId(vehicleId);
    setShowHistoryModal(true);
    // Close mobile drawer so the History modal sits on a clean backdrop.
    setMobileSidebarOpen(false);
  };

  // Export route data as CSV
  const exportRouteCSV = () => {
    if (historyData.length === 0) return;

    const headers = ['التاريخ والوقت', 'خط العرض', 'خط الطول', 'السرعة (كم/س)'];
    const rows = historyData.map(p => [
      dayjs(p.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      p.lat.toFixed(6),
      p.lng.toFixed(6),
      p.speed?.toFixed(0) || '0'
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `route_history_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Export route data as JSON
  const exportRouteJSON = () => {
    if (historyData.length === 0) return;

    const data = {
      exportDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      statistics: routeStatistics,
      positions: historyData.map(p => ({
        timestamp: p.timestamp,
        latitude: p.lat,
        longitude: p.lng,
        speed: p.speed || 0
      }))
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `route_history_${dayjs().format('YYYY-MM-DD_HH-mm')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Get selected vehicle info
  const selectedVehicleInfo = useMemo(() => {
    if (!historyVehicleId) return null;
    return vehicles.find((v: Vehicle) => v.id === historyVehicleId);
  }, [historyVehicleId, vehicles]);

  return (
    <div className="h-[calc(100vh-4rem)] 3xl:h-[calc(100vh-5rem)] 4xl:h-[calc(100vh-6rem)] 5xl:h-[calc(100vh-8rem)] flex" dir="rtl">
      {/* Sidebar — visible on tablet+, hidden on phone (replaced by Drawer). */}
      <div className="hidden md:flex w-72 md:w-80 3xl:w-96 4xl:w-[28rem] 5xl:w-[32rem] bg-white shadow-lg flex-col border-l border-gray-200">
        {/* Header */}
        <div className="p-3 sm:p-4 3xl:p-5 4xl:p-6 5xl:p-8 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3 3xl:mb-4">
            <h2 className="text-base sm:text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl font-semibold text-gray-900">المركبات</h2>
            <div className="flex items-center gap-1 sm:gap-2 3xl:gap-3">
              {/* Alerts UI removed from the sidebar entirely. Alerts now
                  surface only as coloured circles on the map (history
                  mode) — no bell, no badge, no popover here. */}
              <button
                onClick={() => setSoundEnabled((v) => !v)}
                className={clsx(
                  'p-1.5 sm:p-2 3xl:p-3 4xl:p-4 rounded-lg 3xl:rounded-xl transition-colors',
                  soundEnabled ? 'bg-gray-100 text-gray-600' : 'bg-gray-200 text-gray-400'
                )}
                title={soundEnabled ? 'كتم صوت التنبيهات' : 'تفعيل صوت التنبيهات'}
              >
                {soundEnabled
                  ? <SpeakerWaveIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10" />
                  : <SpeakerXMarkIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10" />}
              </button>
              <button
                onClick={() => setIsTracking(!isTracking)}
                className={clsx(
                  'p-1.5 sm:p-2 3xl:p-3 4xl:p-4 rounded-lg 3xl:rounded-xl transition-colors',
                  isTracking ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                )}
                title={isTracking ? 'إيقاف التتبع' : 'تشغيل التتبع'}
              >
                {isTracking ? <PlayIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10" /> : <StopIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10" />}
              </button>
              <button
                onClick={() => refetch()}
                className="p-1.5 sm:p-2 3xl:p-3 4xl:p-4 rounded-lg 3xl:rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                title="تحديث"
              >
                <ArrowPathIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10" />
              </button>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute right-2 sm:right-3 3xl:right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7 text-gray-400" />
            <input
              type="text"
              placeholder="بحث عن مركبة..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-8 sm:pr-10 3xl:pr-12 4xl:pr-14 pl-3 sm:pl-4 py-1.5 sm:py-2 3xl:py-3 4xl:py-4 text-sm sm:text-base 3xl:text-lg 4xl:text-xl 5xl:text-2xl border border-gray-300 rounded-lg 3xl:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Vehicle List */}
        <div className="flex-1 overflow-y-auto">
          {isError ? (
            <div className="m-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm">
              <div className="flex items-start gap-2">
                <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-red-900">تعذّر تحميل المركبات</p>
                  <p className="text-red-700 text-xs mt-1">{(error as any)?.message || 'حدث خطأ في الاتصال بالخادم'}</p>
                  <button
                    onClick={() => refetch()}
                    className="mt-2 px-3 py-1 rounded bg-red-600 text-white text-xs hover:bg-red-700 min-h-[32px]"
                  >
                    إعادة المحاولة
                  </button>
                </div>
              </div>
            </div>
          ) : isLoading ? (
            // Three-row skeleton so the user sees structure immediately
            // instead of an empty spinner; matches the actual row layout.
            <div className="p-3 space-y-3 animate-pulse">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                    <div className="h-2 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchTerm ? 'لا توجد نتائج للبحث' : 'لا توجد مركبات'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredVehicles.map((vehicle: Vehicle) => {
                const position = getVehiclePosition(vehicle.id);
                // "Has a route to draw" = either an active history trace OR
                // an accumulated live tail.
                const hasRoute = historyRoutes.has(vehicle.id) || vehicleRoutes.has(vehicle.id);
                const hasHistory = historyRoutes.has(vehicle.id);
                const isOnline = isVehicleOnline(vehicle.id);
                
                return (
                  <div
                    key={vehicle.id}
                    data-vehicle-row={vehicle.id}
                    className={clsx(
                      'p-3 cursor-pointer transition-colors',
                      selectedVehicle === vehicle.id
                        // A left accent bar, not just a tint: the selection has
                        // to be findable at a glance after the list scrolls.
                        ? 'bg-primary-50 border-r-4 border-primary-500'
                        : 'hover:bg-gray-50'
                    )}
                    onClick={() => handleVehicleSelect(vehicle)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={clsx(
                          'w-10 h-10 rounded-full flex items-center justify-center relative flex-shrink-0',
                          isOnline ? (
                            vehicle.status === 'active' ? 'bg-green-100' :
                            vehicle.status === 'busy' ? 'bg-yellow-100' : 'bg-blue-100'
                          ) : 'bg-gray-100'
                        )}>
                          <TruckIcon className={clsx(
                            'w-5 h-5',
                            isOnline ? (
                              vehicle.status === 'active' ? 'text-green-600' :
                              vehicle.status === 'busy' ? 'text-yellow-600' : 'text-blue-600'
                            ) : 'text-gray-400'
                          )} />
                          {/* Online indicator dot */}
                          {isOnline && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{vehicle.plateNumber}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {vehicleTypeLabel(vehicle.vehicleType)}
                          </p>
                          {(vehicle.model || vehicle.year) && (
                            <p className="text-xs text-gray-400 truncate">
                              {vehicle.model}
                              {vehicle.model && vehicle.year ? ' · ' : ''}
                              {vehicle.year}
                            </p>
                          )}
                          {/* اسم المؤسسة — يساعد المراقب يميّز سريعاً بين
                              مركبات مستشفيات/إدارات مختلفة في القائمة. */}
                          {(vehicle.organization?.nameAr || vehicle.organization?.name) && (
                            <p className="text-[11px] text-primary-600 truncate flex items-center gap-1 mt-0.5">
                              <BuildingOffice2Icon className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{vehicle.organization?.nameAr || vehicle.organization?.name}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className={clsx(
                        // flex-shrink-0 + whitespace-nowrap: the model/year now
                        // sitting on the line above makes the name column wide
                        // enough to squeeze this badge into two wrapped lines.
                        'px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 whitespace-nowrap',
                        isOnline ? (
                          vehicle.status === 'active' ? 'bg-green-100 text-green-800' :
                          vehicle.status === 'busy' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                        ) : 'bg-gray-100 text-gray-600'
                      )}>
                        {isOnline ? (
                          vehicle.status === 'active' ? 'متاح' :
                          vehicle.status === 'busy' ? 'مشغول' : 'متصل'
                        ) : 'غير متصل'}
                      </div>
                    </div>
                    
                    {position ? (
                      <div className="mt-2 space-y-0.5 text-xs text-gray-500">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <SignalIcon className="w-4 h-4" />
                            {position.speed?.toFixed(0) || 0} كم/س
                          </span>
                          <span className="flex items-center gap-1">
                            <ClockIcon className="w-4 h-4" />
                            آخر اتصال: {fmtTime(heardAt(position), 'HH:mm')}
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-400 mr-5">
                          {dayjs(heardAt(position)).fromNow()}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-400 italic">
                        لم يتصل بعد
                      </div>
                    )}

                    {/* Route controls */}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          centerOnVehicle(vehicle.id);
                        }}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-100 text-blue-600 min-h-[32px]"
                        disabled={!position}
                      >
                        <MapPinIcon className="w-3 h-3" />
                        تتبع
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAutoFollow(autoFollow === vehicle.id ? null : vehicle.id);
                        }}
                        className={clsx(
                          'flex items-center gap-1 px-2 py-1 rounded text-xs min-h-[32px]',
                          autoFollow === vehicle.id ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        )}
                        disabled={!position}
                        title={autoFollow === vehicle.id ? 'إيقاف الكاميرا التلقائية' : 'تتبع الكاميرا تلقائياً'}
                      >
                        <ViewfinderCircleIcon className="w-3 h-3" />
                        كاميرا
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRoute(showRoute === vehicle.id ? null : vehicle.id);
                        }}
                        className={clsx(
                          'flex items-center gap-1 px-2 py-1 rounded text-xs min-h-[32px]',
                          showRoute === vehicle.id ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
                        )}
                        disabled={!hasRoute}
                      >
                        <EyeIcon className="w-3 h-3" />
                        المسار
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openHistoryModal(vehicle.id);
                        }}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-purple-100 text-purple-600 min-h-[32px]"
                      >
                        <CalendarDaysIcon className="w-3 h-3" />
                        السجل
                      </button>
                      {hasHistory && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            exitHistoryMode(vehicle.id);
                          }}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-700 min-h-[32px]"
                          title="إغلاق سجل الحركة والعودة للوضع الحي"
                        >
                          <ArrowPathIcon className="w-3 h-3" />
                          مباشر
                        </button>
                      )}
                      {hasRoute && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            clearAllRoutes(vehicle.id);
                          }}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-100 text-red-600"
                        >
                          <TrashIcon className="w-3 h-3" />
                          مسح
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Stats */}
        <div className="p-2 sm:p-4 3xl:p-5 4xl:p-6 border-t border-gray-200 bg-gray-50">
          <div className="grid grid-cols-3 gap-1 sm:gap-2 3xl:gap-4 text-center">
            <div>
              <p className="text-base sm:text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl font-semibold text-green-600">
                {onlineCount}
              </p>
              <p className="text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl text-gray-500">متصل</p>
            </div>
            <div>
              <p className="text-base sm:text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl font-semibold text-yellow-600">
                {vehicles.filter((v: Vehicle) => v.status === 'busy').length}
              </p>
              <p className="text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl text-gray-500">مشغول</p>
            </div>
            <div>
              <p className="text-base sm:text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl font-semibold text-gray-600">
                {offlineCount}
              </p>
              <p className="text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl text-gray-500">غير متصل</p>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div ref={mapWrapRef} className="flex-1 relative bg-white">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          className="h-full w-full"
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController
            center={mapCenter}
            zoom={mapZoom}
            shouldUpdate={shouldUpdateMap}
            onUpdateComplete={handleMapUpdateComplete}
            fitBounds={fitBoundsData}
            onFitComplete={() => setFitBoundsData(null)}
            onMapReady={(m) => { leafletMapRef.current = m; }}
            onUserMove={(c, z) => {
              setMapCenter(c);
              setMapZoom(z);
              saveMapState(c, z);
            }}
          />

          {/* Vehicle markers — wrapped in a cluster group on dense fleets so
              we don't bury the map under overlapping markers. Disabled past
              zoom 14 so close-up navigation stays uncluttered. */}
          {(() => {
            const markers = filteredVehicles
              .map((vehicle: Vehicle) => {
                const position = getVehiclePosition(vehicle.id);
                if (!position) return null;
                const isOnline = isVehicleOnline(vehicle.id);
                return (
                  <Marker
                    key={vehicle.id}
                    position={posTuple(vehicle.id, position.latitude, position.longitude)}
                    icon={createVehicleIcon(vehicle.vehicleType, vehicle.status, isOnline)}
                    // Draw it above its neighbours — a highlighted marker
                    // hidden under an overlapping one defeats the purpose.
                    //
                    // Leaflet derives a marker's z-index from its projected Y,
                    // so the gap between two markers grows with zoom and with
                    // how far apart they are: measured at ~1,300 for a fleet
                    // spanning 0.1° at zoom 16, and it scales from there. The
                    // offset has to clear that whole range, not a token 1000.
                    zIndexOffset={selectedVehicle === vehicle.id ? 100_000 : 0}
                    eventHandlers={{ click: () => handleVehicleSelect(vehicle) }}
                  >
                    <Popup>
                      <div className="text-right" dir="rtl">
                        <h3 className="font-semibold text-lg">{vehicle.plateNumber}</h3>
                        <p className="text-gray-600">{vehicleTypeLabel(vehicle.vehicleType)}</p>
                        {(vehicle.model || vehicle.year) && (
                          <p className="text-gray-700 text-sm">
                            {vehicle.model || '—'}
                            {vehicle.year && <span className="text-gray-500"> · موديل {vehicle.year}</span>}
                          </p>
                        )}
                        {(vehicle.organization?.nameAr || vehicle.organization?.name) && (
                          <p className="text-xs text-primary-600 flex items-center gap-1 mt-0.5">
                            <BuildingOffice2Icon className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{vehicle.organization?.nameAr || vehicle.organization?.name}</span>
                          </p>
                        )}
                        <div className={clsx(
                          'inline-block px-2 py-1 rounded-full text-xs font-medium mt-1',
                          isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        )}>
                          {isOnline ? '● متصل' : '○ غير متصل'}
                        </div>
                        <div className="mt-2 space-y-1 text-sm">
                          <p>السرعة: {position.speed?.toFixed(0) || 0} كم/س</p>
                          <p>آخر اتصال: {fmtTime(heardAt(position))}</p>
                          <p>الإحداثيات: {position.latitude.toFixed(5)}, {position.longitude.toFixed(5)}</p>
                          {(position as any).ignition !== undefined && (
                            <p>المحرك: <span className={(position as any).ignition ? 'text-green-700 font-semibold' : 'text-gray-500'}>
                              {(position as any).ignition ? 'يعمل' : 'متوقّف'}
                            </span></p>
                          )}
                          {(position as any).batteryLevel != null && (
                            <p>البطارية: {(position as any).batteryLevel}%</p>
                          )}
                          {(position as any).externalVoltageMv != null && (
                            <p>الفولتية الخارجية: {((position as any).externalVoltageMv / 1000).toFixed(1)}V</p>
                          )}
                          {(position as any).gsmSignal != null && (
                            <p>إشارة GSM: {(position as any).gsmSignal}/5</p>
                          )}
                          {(position as any).totalOdometerKm != null && (
                            <p>العدّاد: {((position as any).totalOdometerKm).toLocaleString()} كم</p>
                          )}
                          <p className="pt-1 border-t mt-2">
                            {/* react-router Link, not a plain <a> — a
                                full-page reload was breaking the SPA
                                state (and triggering a re-auth flash). */}
                            <Link to={`/vehicles/${vehicle.id}`} className="text-blue-600 hover:underline">
                              عرض كل التفاصيل ←
                            </Link>
                          </p>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })
              .filter(Boolean);

            if (enableClustering && markers.length >= 20) {
              return (
                <MarkerClusterGroup
                  chunkedLoading
                  showCoverageOnHover={false}
                  spiderfyOnMaxZoom
                  disableClusteringAtZoom={14}
                  maxClusterRadius={60}
                >
                  {markers}
                </MarkerClusterGroup>
              );
            }
            return <>{markers}</>;
          })()}

          {/* Selection ring — a layer of its own, deliberately outside the
              cluster group so it is never clustered and never touches the
              vehicle marker it sits under. Non-interactive so clicks fall
              through to the marker. */}
          {(() => {
            if (!selectedVehicle) return null;
            const p = vehiclePositions.get(selectedVehicle);
            if (!p) return null;
            return (
              <Marker
                key={`selection-halo-${selectedVehicle}`}
                position={posTuple(`halo:${selectedVehicle}`, p.latitude, p.longitude)}
                icon={SELECTION_HALO_ICON}
                interactive={false}
                zIndexOffset={-1000}
              />
            );
          })()}

          {/* Fixed ambulance stations and deployment points. Rendered before
              the routes so vehicle traces draw over them, and outside the
              vehicle cluster so a dense corridor never absorbs a live
              ambulance into a station cluster. */}
          {showStations && visibleStations.map((sp) => (
            <Marker
              key={`station-${sp.id}`}
              position={[sp.latitude, sp.longitude]}
              icon={createStationIcon(sp.kind)}
              zIndexOffset={-500}
            >
              <Popup>
                <div className="text-right min-w-[200px]" dir="rtl">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="inline-block w-3 h-3 rounded-sm shrink-0"
                      style={{ backgroundColor: (STATION_STYLE[sp.kind] || STATION_STYLE.checkpoint)!.color }}
                    />
                    <h3 className="font-semibold text-base">{sp.name}</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    {(STATION_STYLE[sp.kind] || STATION_STYLE.checkpoint)!.label}
                    {sp.corridor && <> • {sp.corridor}</>}
                  </p>
                  {sp.description && (
                    <p className="text-sm text-gray-700 mb-2">{sp.description}</p>
                  )}
                  <p className="text-xs text-gray-500" dir="ltr">
                    {sp.latitude.toFixed(5)}, {sp.longitude.toFixed(5)}
                  </p>
                  {/* The source map surveyed several of these by eye; saying so
                      is more useful than presenting every pin as exact. */}
                  {sp.accuracy === 'estimated' && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200
                                  rounded px-2 py-1 mt-2">
                      إحداثيات تقديرية — تحتاج تدقيقاً ميدانياً
                    </p>
                  )}
                  {sp.accuracy === 'high' && (
                    <p className="text-xs text-green-700 mt-2">إحداثيات بدقة عالية</p>
                  )}
                  <a
                    href={`https://www.google.com/maps?q=${sp.latitude},${sp.longitude}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-primary-600 text-xs hover:underline block mt-2"
                  >
                    فتح في خرائط جوجل
                  </a>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Vehicle routes — prefer the dedicated history trace if present,
              otherwise fall back to the live tail. Live tail can update while
              history is shown without overwriting history. */}
          {showRoute && (() => {
            const route = historyRoutes.get(showRoute) || vehicleRoutes.get(showRoute);
            if (!route || route.positions.length === 0) return null;
            const pts = route.positions;
            const isHistory = historyRoutes.has(showRoute);

            if (colorBySpeed && isHistory && pts.length > 1) {
              // Split into per-segment polylines so each can carry its own
              // colour. Slow segments green, mid yellow, fast red.
              return (
                <>
                  {pts.slice(0, -1).map((p, i) => {
                    const next = pts[i + 1];
                    return (
                      <Polyline
                        key={i}
                        positions={[[p.lat, p.lng], [next.lat, next.lng]]}
                        color={speedColor(next.speed)}
                        weight={4}
                        opacity={0.85}
                      />
                    );
                  })}
                  <Circle
                    center={[pts[0].lat, pts[0].lng]}
                    radius={25}
                    pathOptions={{ color: 'green', fillColor: 'green', fillOpacity: 0.8 }}
                  >
                    <Popup>
                      <div dir="rtl" className="text-right">
                        <strong>البداية</strong><br />
                        {fmtTime(pts[0].timestamp, 'YYYY-MM-DD HH:mm')}
                      </div>
                    </Popup>
                  </Circle>
                  <Circle
                    center={[pts[pts.length - 1].lat, pts[pts.length - 1].lng]}
                    radius={25}
                    pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.8 }}
                  >
                    <Popup>
                      <div dir="rtl" className="text-right">
                        <strong>النهاية</strong><br />
                        {fmtTime(pts[pts.length - 1].timestamp, 'YYYY-MM-DD HH:mm')}
                      </div>
                    </Popup>
                  </Circle>
                </>
              );
            }

            return (
              <>
                <Polyline
                  positions={pts.map(p => [p.lat, p.lng])}
                  color="#3B82F6"
                  weight={3}
                  opacity={0.7}
                />
                {pts.length > 0 && (
                  <Circle
                    center={[pts[0].lat, pts[0].lng]}
                    radius={20}
                    pathOptions={{ color: 'green', fillColor: 'green', fillOpacity: 0.8 }}
                  />
                )}
                {pts.length > 1 && (
                  <Circle
                    center={[pts[pts.length - 1].lat, pts[pts.length - 1].lng]}
                    radius={20}
                    pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.8 }}
                  />
                )}
              </>
            );
          })()}

          {/* Alert markers — drawn only when a history trace is being
              viewed for a vehicle. Live mode keeps the map uncluttered;
              alerts are intentionally NOT shown in the sidebar at all.
              Alerts are deduplicated by rounded coordinate (5 decimals ≈
              1m) so a stream of repeats at one stop becomes a single
              circle with a "+N" badge in the popup. Circle color follows
              the worst severity in the cluster. */}
          {showRoute && historyAlerts.has(showRoute) && (() => {
            const list = historyAlerts.get(showRoute) || [];
            // group by rounded (lat,lng) — repeats at the same spot
            // collapse into one circle.
            const groups = new Map<string, Alert[]>();
            for (const a of list) {
              if (!a.latitude || !a.longitude) continue;
              const key = `${parseFloat(a.latitude).toFixed(5)},${parseFloat(a.longitude).toFixed(5)}`;
              const arr = groups.get(key) || [];
              arr.push(a);
              groups.set(key, arr);
            }
            return Array.from(groups.entries()).map(([key, group]) => {
              // Pick the worst severity to colour the circle.
              const worst = group.reduce((a, b) =>
                severityRank(b.severity) > severityRank(a.severity) ? b : a
              );
              const color = severityHex(worst.severity);
              const lat = parseFloat(group[0].latitude!);
              const lng = parseFloat(group[0].longitude!);
              // Slightly larger radius / heavier weight for higher severity
              // so the eye picks up criticals first.
              const radius = worst.severity === 'critical' ? 70 : worst.severity === 'high' ? 55 : 45;
              return (
                <Circle
                  key={key}
                  center={[lat, lng]}
                  radius={radius}
                  pathOptions={{
                    color,
                    fillColor: color,
                    fillOpacity: 0.35,
                    weight: 2,
                  }}
                >
                  <Popup maxWidth={320}>
                    <div className="text-right" dir="rtl" style={{ minWidth: 220 }}>
                      <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-bold text-white"
                          style={{ backgroundColor: color }}
                        >
                          {worst.severity === 'critical' ? 'خطر'
                            : worst.severity === 'high' ? 'عالٍ'
                            : worst.severity === 'medium' ? 'متوسط'
                            : 'منخفض'}
                        </span>
                        {group.length > 1 && (
                          <span className="text-xs text-gray-500">+{group.length - 1} تنبيهات مكرّرة</span>
                        )}
                      </div>
                      {group.slice(0, 5).map((a, i) => (
                        <div key={a.id} className={clsx('py-1', i > 0 && 'border-t border-gray-100')}>
                          <p className="font-semibold text-sm">
                            <span className="ml-1">{getAlertIcon(a.type)}</span>
                            {a.messageAr || a.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{fmtTime(a.createdAt, 'YYYY-MM-DD HH:mm:ss')}</p>
                        </div>
                      ))}
                      {group.length > 5 && (
                        <p className="text-xs text-gray-400 mt-1 text-center">و {group.length - 5} تنبيه آخر…</p>
                      )}
                    </div>
                  </Popup>
                </Circle>
              );
            });
          })()}
        </MapContainer>

        {/* Loading overlay — shown while the initial fetch is in flight,
            or while the fetch has completed but returned zero vehicles
            with positions (e.g. the cache held a stale empty list).
            Placed at z-[900] so it sits above the map tiles but below
            map controls (z-[1000]) and modals (>= z-[2000]).
            Once the user manually pans/zooms or the data arrives, the
            overlay disappears automatically. */}
        {(isLoading || (vehiclesData && (vehiclesData.data?.length ?? 0) > 0 && vehiclePositions.size === 0)) && !isError && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[900] bg-white/95 rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 border border-gray-200">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent" />
            <span className="text-sm font-medium text-gray-700">جاري تحميل المركبات…</span>
          </div>
        )}

        {/* Mobile top bar — hamburger + quick actions, only on phones. */}
        <div className="md:hidden absolute top-2 right-2 left-2 z-[1000] flex items-center gap-2">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="bg-white p-2.5 rounded-lg shadow-md min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="القائمة"
            aria-label="فتح القائمة"
          >
            <Bars3Icon className="w-6 h-6 text-gray-700" />
          </button>
          <div className="flex-1" />
          {showStatisticsPanel && routeStatistics && (
            <button
              onClick={() => setMobileStatsOpen(true)}
              className="bg-white p-2.5 rounded-lg shadow-md min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="إحصائيات المسار"
            >
              <ChartBarIcon className="w-6 h-6 text-primary-600" />
            </button>
          )}
          {/* Alerts badge removed from the mobile top bar to match the
              desktop sidebar — alerts are visible only as map circles
              in history mode. */}
        </div>

        {/* Map controls */}
        <div className="absolute top-4 left-4 md:top-4 md:left-4 z-[1000] flex flex-col gap-2 mt-14 md:mt-0">
          <button
            onClick={() => {
              setMapCenter([32.1194, 20.0868]);
              setMapZoom(12);
              setShouldUpdateMap(true);
            }}
            className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 min-h-[40px] min-w-[40px] flex items-center justify-center"
            title="العودة للمركز"
          >
            <MapPinIcon className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={() => {
              // Fit to all vehicles with positions
              const positions = Array.from(vehiclePositions.values());
              if (positions.length === 1) {
                setMapCenter([positions[0].latitude, positions[0].longitude]);
                setMapZoom(14);
                setShouldUpdateMap(true);
              } else if (positions.length > 1) {
                const bounds = positions.map(p => [p.latitude, p.longitude] as [number, number]);
                setFitBoundsData(bounds);
              }
            }}
            className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 min-h-[40px] min-w-[40px] flex items-center justify-center"
            title="عرض جميع المركبات"
          >
            <TruckIcon className="w-5 h-5 text-gray-600" />
          </button>
          {/* Stations & deployment points layer. A dropdown rather than a bare
              on/off toggle because the corridors carry three distinct kinds
              and an operator usually wants just one of them. */}
          {(stationData?.items?.length ?? 0) > 0 && (
            <div className="relative group">
              <button
                onClick={() => setShowStations((v) => !v)}
                className={clsx(
                  'p-2 rounded-lg shadow-md min-h-[40px] min-w-[40px] flex items-center justify-center',
                  showStations ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-600 hover:bg-gray-50',
                )}
                title={showStations ? 'إخفاء نقاط الإسعاف والتمركزات' : 'إظهار نقاط الإسعاف والتمركزات'}
              >
                <BuildingOffice2Icon className="w-5 h-5" />
              </button>
              {showStations && (
                <div className="absolute top-0 right-full mr-2 hidden group-hover:block
                                bg-white rounded-lg shadow-lg p-2 w-56" dir="rtl">
                  <p className="text-xs font-semibold text-gray-700 mb-2 px-1">
                    نقاط الإسعاف والتمركزات ({visibleStations.length})
                  </p>
                  <div className="space-y-1">
                    <button
                      onClick={() => setStationKindFilter('')}
                      className={clsx('w-full text-right px-2 py-1.5 rounded text-xs',
                        !stationKindFilter ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50')}
                    >
                      الكل ({stationData?.items.length ?? 0})
                    </button>
                    {Object.entries(STATION_STYLE).map(([kind, style]) => {
                      const n = stationData?.meta?.byKind?.[kind] ?? 0;
                      if (!n) return null;
                      return (
                        <button
                          key={kind}
                          onClick={() => setStationKindFilter(kind)}
                          className={clsx('w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs',
                            stationKindFilter === kind
                              ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50')}
                        >
                          <span className="w-3 h-3 rounded-sm shrink-0"
                                style={{ backgroundColor: style.color }} />
                          <span>{style.label} ({n})</span>
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => {
                      const pts = visibleStations.map((sp) =>
                        [sp.latitude, sp.longitude] as [number, number]);
                      if (pts.length) setFitBoundsData(pts);
                    }}
                    className="w-full mt-2 pt-2 border-t border-gray-100 text-xs text-primary-600 hover:underline"
                  >
                    تكبير لعرض كل النقاط
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Fullscreen toggle. Wraps the map area only (not the
              sidebar), so on exit the rest of the UI is intact.
              iOS Safari < 16.4 silently rejects requestFullscreen — the
              catch in toggleFullscreen swallows it, no crash. */}
          <button
            onClick={toggleFullscreen}
            className={clsx(
              'p-2 rounded-lg shadow-md min-h-[40px] min-w-[40px] flex items-center justify-center',
              isFullscreen ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-600 hover:bg-gray-50'
            )}
            title={isFullscreen ? 'الخروج من العرض الكامل' : 'عرض بكامل الشاشة'}
          >
            {isFullscreen
              ? <ArrowsPointingInIcon className="w-5 h-5" />
              : <ArrowsPointingOutIcon className="w-5 h-5" />}
          </button>
          {/* Re-fit camera to the currently displayed route (history or live tail). */}
          {showRoute && (() => {
            const route = historyRoutes.get(showRoute) || vehicleRoutes.get(showRoute);
            if (!route || route.positions.length < 2) return null;
            return (
              <button
                onClick={() => {
                  const bounds = route.positions.map(p => [p.lat, p.lng] as [number, number]);
                  setFitBoundsData(bounds);
                }}
                className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 min-h-[40px] min-w-[40px] flex items-center justify-center"
                title="عرض المسار كاملاً"
              >
                <ArrowsPointingOutIcon className="w-5 h-5 text-primary-600" />
              </button>
            );
          })()}
          {/* Toggle clustering. Useful on very dense fleets. */}
          <button
            onClick={() => setEnableClustering((v) => !v)}
            className={clsx(
              'p-2 rounded-lg shadow-md min-h-[40px] min-w-[40px] flex items-center justify-center',
              enableClustering ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-600'
            )}
            title={enableClustering ? 'إيقاف تجميع المركبات' : 'تجميع المركبات'}
          >
            <ViewfinderCircleIcon className="w-5 h-5" />
          </button>
          {/* Toggle speed-based colouring of the history polyline. */}
          {showRoute && historyRoutes.has(showRoute) && (
            <button
              onClick={() => setColorBySpeed((v) => !v)}
              className={clsx(
                'p-2 rounded-lg shadow-md min-h-[40px] min-w-[40px] flex items-center justify-center',
                colorBySpeed ? 'bg-amber-100 text-amber-700' : 'bg-white text-gray-600'
              )}
              title={colorBySpeed ? 'تلوين موحّد' : 'تلوين حسب السرعة'}
            >
              <SignalIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Tracking status indicator */}
        <div className={clsx(
          'absolute bottom-4 left-4 z-[1000] px-3 py-2 rounded-lg shadow-md flex items-center gap-2',
          isTracking ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
        )}>
          <div className={clsx(
            'w-2 h-2 rounded-full',
            isTracking ? 'bg-white animate-pulse' : 'bg-gray-300'
          )} />
          <span className="text-sm font-medium">
            {isTracking ? 'التتبع المباشر نشط' : 'التتبع متوقف'}
          </span>
        </div>

        {/* Online vehicles indicator */}
        <div className="absolute bottom-4 right-4 z-[1000] px-3 py-2 rounded-lg shadow-md bg-white flex items-center gap-2">
          <WifiIcon className="w-4 h-4 text-green-500" />
          <span className="text-sm font-medium text-gray-700">
            {onlineCount} مركبة متصلة
          </span>
        </div>

        {/* Route Statistics Panel */}
        {showStatisticsPanel && routeStatistics && (
          <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg p-4 w-80" dir="rtl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold text-gray-900">إحصائيات المسار</h3>
              </div>
              <button
                onClick={() => setShowStatisticsPanel(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {selectedVehicleInfo && (
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-sm text-gray-600">المركبة: <span className="font-medium text-gray-900">{selectedVehicleInfo.plateNumber}</span></p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{routeStatistics.totalPoints}</p>
                <p className="text-xs text-gray-600">نقطة</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{routeStatistics.totalDistance}</p>
                <p className="text-xs text-gray-600">كم</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-yellow-600">{routeStatistics.avgSpeed}</p>
                <p className="text-xs text-gray-600">متوسط السرعة</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-600">{routeStatistics.maxSpeed}</p>
                <p className="text-xs text-gray-600">أقصى سرعة</p>
              </div>
            </div>

            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <span className="text-gray-600">المدة الإجمالية:</span>
                <span className="font-medium">{Math.floor(routeStatistics.totalDuration / 60)} ساعة {routeStatistics.totalDuration % 60} دقيقة</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">البداية:</span>
                <span className="font-medium">{routeStatistics.startTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">النهاية:</span>
                <span className="font-medium">{routeStatistics.endTime}</span>
              </div>
            </div>

            {/* Export buttons */}
            <div className="flex gap-2">
              <button
                onClick={exportRouteCSV}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                CSV
              </button>
              <button
                onClick={exportRouteJSON}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              >
                <DocumentTextIcon className="w-4 h-4" />
                JSON
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile sidebar drawer — slides in from the right (RTL). Shares
          the same vehicle list / alerts / footer as desktop sidebar by
          re-rendering the same components inside a Dialog.
          z-[3000] + `fixed` so the Drawer sits above the Leaflet panes
          (Leaflet uses up to z-700 internally) and any transformed
          parents that would otherwise trap a `relative` Dialog. */}
      <Dialog
        open={isMobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        className="md:hidden"
        style={{ position: 'fixed', inset: 0, zIndex: 3000 }}
      >
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <Dialog.Panel className="w-screen max-w-sm bg-white flex flex-col" dir="rtl">
            <div className="flex items-center justify-between p-3 border-b">
              <h2 className="text-lg font-semibold">المركبات</h2>
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <XMarkIcon className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {/* Alerts list removed — only shown as coloured circles
                  on the map in history mode. */}
              <div className="p-3">
                <input
                  type="text"
                  placeholder="بحث عن مركبة..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="divide-y divide-gray-100">
                {isError ? (
                  <div className="m-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm">
                    <p className="font-semibold text-red-900">تعذّر التحميل</p>
                    <button onClick={() => refetch()} className="mt-2 px-3 py-1 rounded bg-red-600 text-white text-xs">
                      إعادة المحاولة
                    </button>
                  </div>
                ) : isLoading ? (
                  <div className="p-3 space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="h-12 bg-gray-200 rounded-lg" />
                    ))}
                  </div>
                ) : filteredVehicles.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">لا توجد مركبات</div>
                ) : (
                  filteredVehicles.map((vehicle: Vehicle) => {
                    const position = getVehiclePosition(vehicle.id);
                    const isOnline = isVehicleOnline(vehicle.id);
                    const hasRoute = historyRoutes.has(vehicle.id) || vehicleRoutes.has(vehicle.id);
                    const hasHistory = historyRoutes.has(vehicle.id);
                    return (
                      <div
                        key={vehicle.id}
                        data-vehicle-row={vehicle.id}
                        className={clsx(
                          'p-3 transition-colors',
                          selectedVehicle === vehicle.id
                            ? 'bg-primary-50 border-r-4 border-primary-500'
                            : 'hover:bg-gray-50'
                        )}
                      >
                        <button
                          onClick={() => {
                            handleVehicleSelect(vehicle);
                            setMobileSidebarOpen(false);
                          }}
                          className="w-full text-right flex items-center justify-between min-h-[44px]"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={clsx(
                              'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                              isOnline ? 'bg-green-100' : 'bg-gray-100'
                            )}>
                              <TruckIcon className={clsx('w-5 h-5', isOnline ? 'text-green-600' : 'text-gray-400')} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{vehicle.plateNumber}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {vehicleTypeLabel(vehicle.vehicleType)}
                              </p>
                              {(vehicle.model || vehicle.year) && (
                                <p className="text-[11px] text-gray-400 truncate">
                                  {vehicle.model}
                                  {vehicle.model && vehicle.year ? ' · ' : ''}
                                  {vehicle.year}
                                </p>
                              )}
                              {(vehicle.organization?.nameAr || vehicle.organization?.name) && (
                                <p className="text-[11px] text-primary-600 truncate flex items-center gap-1">
                                  <BuildingOffice2Icon className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{vehicle.organization?.nameAr || vehicle.organization?.name}</span>
                                </p>
                              )}
                              {position ? (
                                <>
                                  <p className="text-xs text-gray-500 truncate">
                                    {position.speed?.toFixed(0) || 0} كم/س · آخر اتصال: {fmtTime(heardAt(position), 'HH:mm')}
                                  </p>
                                  <p className="text-[10px] text-gray-400 truncate">
                                    {dayjs(heardAt(position)).fromNow()} · {isOnline ? 'متصل' : 'غير متصل'}
                                  </p>
                                </>
                              ) : (
                                <p className="text-xs text-gray-400 italic truncate">لم يتصل بعد</p>
                              )}
                            </div>
                          </div>
                        </button>
                        {/* Same control row as desktop sidebar so phone users
                            get history / route / auto-follow / clear. */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => {
                              centerOnVehicle(vehicle.id);
                              setMobileSidebarOpen(false);
                            }}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-100 text-blue-600 min-h-[36px]"
                            disabled={!position}
                          >
                            <MapPinIcon className="w-3 h-3" />
                            تتبع
                          </button>
                          <button
                            onClick={() => {
                              setAutoFollow(autoFollow === vehicle.id ? null : vehicle.id);
                              setMobileSidebarOpen(false);
                            }}
                            className={clsx(
                              'flex items-center gap-1 px-2 py-1 rounded text-xs min-h-[36px]',
                              autoFollow === vehicle.id ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            )}
                            disabled={!position}
                          >
                            <ViewfinderCircleIcon className="w-3 h-3" />
                            كاميرا
                          </button>
                          <button
                            onClick={() => setShowRoute(showRoute === vehicle.id ? null : vehicle.id)}
                            className={clsx(
                              'flex items-center gap-1 px-2 py-1 rounded text-xs min-h-[36px]',
                              showRoute === vehicle.id ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
                            )}
                            disabled={!hasRoute}
                          >
                            <EyeIcon className="w-3 h-3" />
                            المسار
                          </button>
                          <button
                            onClick={() => openHistoryModal(vehicle.id)}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-purple-100 text-purple-600 min-h-[36px]"
                          >
                            <CalendarDaysIcon className="w-3 h-3" />
                            السجل
                          </button>
                          {hasHistory && (
                            <button
                              onClick={() => exitHistoryMode(vehicle.id)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-700 min-h-[36px]"
                            >
                              <ArrowPathIcon className="w-3 h-3" />
                              مباشر
                            </button>
                          )}
                          {hasRoute && (
                            <button
                              onClick={() => clearAllRoutes(vehicle.id)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-100 text-red-600 min-h-[36px]"
                            >
                              <TrashIcon className="w-3 h-3" />
                              مسح
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Mobile stats drawer — bottom sheet for route statistics. */}
      <Dialog
        open={isMobileStatsOpen}
        onClose={() => setMobileStatsOpen(false)}
        className="md:hidden"
        style={{ position: 'fixed', inset: 0, zIndex: 3000 }}
      >
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-x-0 bottom-0">
          <Dialog.Panel className="bg-white rounded-t-2xl p-4 max-h-[80vh] overflow-y-auto" dir="rtl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5 text-primary-600" />
                إحصائيات المسار
              </h3>
              <button onClick={() => setMobileStatsOpen(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            {routeStatistics && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{routeStatistics.totalPoints}</p>
                    <p className="text-xs text-gray-600">نقطة</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{routeStatistics.totalDistance}</p>
                    <p className="text-xs text-gray-600">كم</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{routeStatistics.avgSpeed}</p>
                    <p className="text-xs text-gray-600">متوسط</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{routeStatistics.maxSpeed}</p>
                    <p className="text-xs text-gray-600">أقصى</p>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">المدة:</span><span>{Math.floor(routeStatistics.totalDuration / 60)}س {routeStatistics.totalDuration % 60}د</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">البداية:</span><span>{routeStatistics.startTime}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">النهاية:</span><span>{routeStatistics.endTime}</span></div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={exportRouteCSV} className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm">CSV</button>
                  <button onClick={exportRouteJSON} className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-lg text-sm">JSON</button>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]" dir="rtl">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <h3 className="text-lg font-semibold mb-4">مراجعة سجل الحركة</h3>
            
            {selectedVehicleInfo && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">المركبة: <span className="font-medium text-gray-900">{selectedVehicleInfo.plateNumber}</span></p>
                <p className="text-sm text-gray-600">النوع: <span className="font-medium text-gray-900">{vehicleTypeLabel(selectedVehicleInfo.vehicleType)}</span></p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
                <input
                  type="datetime-local"
                  value={historyDateRange.start}
                  onChange={(e) => setHistoryDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
                <input
                  type="datetime-local"
                  value={historyDateRange.end}
                  onChange={(e) => setHistoryDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Quick date buttons */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setHistoryDateRange({
                    start: dayjs().subtract(1, 'hour').format('YYYY-MM-DDTHH:mm'),
                    end: dayjs().format('YYYY-MM-DDTHH:mm'),
                  })}
                  className="px-3 py-1 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  آخر ساعة
                </button>
                <button
                  onClick={() => setHistoryDateRange({
                    start: dayjs().subtract(6, 'hour').format('YYYY-MM-DDTHH:mm'),
                    end: dayjs().format('YYYY-MM-DDTHH:mm'),
                  })}
                  className="px-3 py-1 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  آخر 6 ساعات
                </button>
                <button
                  onClick={() => setHistoryDateRange({
                    start: dayjs().subtract(1, 'day').format('YYYY-MM-DDTHH:mm'),
                    end: dayjs().format('YYYY-MM-DDTHH:mm'),
                  })}
                  className="px-3 py-1 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  آخر يوم
                </button>
                <button
                  onClick={() => setHistoryDateRange({
                    start: dayjs().subtract(7, 'day').format('YYYY-MM-DDTHH:mm'),
                    end: dayjs().format('YYYY-MM-DDTHH:mm'),
                  })}
                  className="px-3 py-1 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  آخر أسبوع
                </button>
                <button
                  onClick={() => setHistoryDateRange({
                    start: dayjs().subtract(15, 'day').format('YYYY-MM-DDTHH:mm'),
                    end: dayjs().format('YYYY-MM-DDTHH:mm'),
                  })}
                  className="px-3 py-1 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  آخر 15 يوم
                </button>
              </div>
            </div>

            {historyError && (
              <div className="mt-4 p-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
                <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
                <span>{historyError}</span>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => historyVehicleId && loadHistoryData(historyVehicleId)}
                disabled={historyLoading}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[44px]"
              >
                {historyLoading ? 'جاري التحميل...' : 'عرض السجل'}
              </button>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
